/**
 * Mock data for order service
 */

import {
  Order,
  OrderStatus,
  ShippingMethod,
  BillingMethod,
  PaymentMethod,
  PaymentStatus,
  OrderCustomer,
  OrderItem,
  OrderShipping,
  OrderBilling,
  OrderAddress,
  OrderPayment,
  OrderTotal,
} from "./order.types";

export const mockOrderAddress: OrderAddress = {
  firstName: "John",
  lastName: "Doe",
  company: "Tech Solutions Inc.",
  address1: "123 Main Street",
  address2: "Suite 100",
  city: "San Francisco",
  state: "CA",
  postalCode: "94105",
  country: "United States",
  phone: "+1-555-0123",
};

export const mockOrderCustomer: OrderCustomer = {
  id: "customer-1",
  email: "john.doe@example.com",
  firstName: "John",
  lastName: "Doe",
  phone: "+1-555-0123",
  company: "Tech Solutions Inc.",
};

export const mockOrderItems: OrderItem[] = [
  {
    id: "item-1",
    productId: "prod-1",
    productName: "Wireless Bluetooth Headphones",
    productSku: "WBH-001",
    quantity: 2,
    unitPrice: 199.99,
    totalPrice: 399.98,
    discount: 20.0,
    tax: 30.4,
  },
  {
    id: "item-2",
    productId: "prod-2",
    productName: "Smart Watch Series 5",
    productSku: "SWS-005",
    quantity: 1,
    unitPrice: 299.99,
    totalPrice: 299.99,
    discount: 0,
    tax: 24.0,
  },
];

export const mockOrderShipping: OrderShipping = {
  method: ShippingMethod.EXPRESS,
  address: mockOrderAddress,
  trackingNumber: "1Z999AA1234567890",
  estimatedDelivery: "2024-01-20T00:00:00Z",
  cost: 15.99,
  notes: "Fragile items - handle with care",
};

export const mockOrderBilling: OrderBilling = {
  address: mockOrderAddress,
  method: BillingMethod.INVOICE,
  notes: "Net 30 payment terms",
};

export const mockOrderPayment: OrderPayment = {
  method: PaymentMethod.CREDIT_CARD,
  status: PaymentStatus.COMPLETED,
  transactionId: "txn_1234567890",
  amount: 730.36,
  currency: "USD",
  processedAt: "2024-01-15T10:30:00Z",
};

export const mockOrderTotal: OrderTotal = {
  subtotal: 699.97,
  tax: 54.4,
  shipping: 15.99,
  discount: 20.0,
  total: 750.36,
  currency: "USD",
};

export const mockOrder: Order = {
  id: "order-1",
  orderNumber: "ORD-2024-001",
  customer: mockOrderCustomer,
  items: mockOrderItems,
  shipping: mockOrderShipping,
  billing: mockOrderBilling,
  payment: mockOrderPayment,
  status: OrderStatus.SHIPPED,
  total: mockOrderTotal,
  notes: "Customer requested express shipping",
  tags: ["priority", "express"],
  createdAt: "2024-01-15T10:00:00Z",
  updatedAt: "2024-01-16T14:30:00Z",
  shippedAt: "2024-01-16T14:30:00Z",
};

export const mockOrders: Order[] = [
  mockOrder,
  {
    id: "order-2",
    orderNumber: "ORD-2024-002",
    customer: {
      id: "customer-2",
      email: "jane.smith@example.com",
      firstName: "Jane",
      lastName: "Smith",
      phone: "+1-555-0456",
      company: "Design Studio LLC",
    },
    items: [
      {
        id: "item-3",
        productId: "prod-3",
        productName: "Mechanical Gaming Keyboard",
        productSku: "MGK-003",
        quantity: 1,
        unitPrice: 149.99,
        totalPrice: 149.99,
        discount: 0,
        tax: 12.0,
      },
    ],
    shipping: {
      method: ShippingMethod.STANDARD,
      address: {
        firstName: "Jane",
        lastName: "Smith",
        company: "Design Studio LLC",
        address1: "456 Oak Avenue",
        city: "Los Angeles",
        state: "CA",
        postalCode: "90210",
        country: "United States",
        phone: "+1-555-0456",
      },
      trackingNumber: "1Z999BB987654321",
      estimatedDelivery: "2024-01-22T00:00:00Z",
      cost: 8.99,
    },
    billing: {
      address: {
        firstName: "Jane",
        lastName: "Smith",
        company: "Design Studio LLC",
        address1: "456 Oak Avenue",
        city: "Los Angeles",
        state: "CA",
        postalCode: "90210",
        country: "United States",
        phone: "+1-555-0456",
      },
      method: BillingMethod.CREDIT_CARD,
    },
    payment: {
      method: PaymentMethod.CREDIT_CARD,
      status: PaymentStatus.COMPLETED,
      transactionId: "txn_0987654321",
      amount: 170.98,
      currency: "USD",
      processedAt: "2024-01-14T16:45:00Z",
    },
    total: {
      subtotal: 149.99,
      tax: 12.0,
      shipping: 8.99,
      discount: 0,
      total: 170.98,
      currency: "USD",
    },
    status: OrderStatus.DELIVERED,
    notes: "Delivered to front desk",
    tags: ["standard"],
    createdAt: "2024-01-14T16:30:00Z",
    updatedAt: "2024-01-18T11:20:00Z",
    shippedAt: "2024-01-15T09:00:00Z",
    deliveredAt: "2024-01-18T11:20:00Z",
  },
  {
    id: "order-3",
    orderNumber: "ORD-2024-003",
    customer: {
      id: "customer-3",
      email: "admin@company.com",
      firstName: "Admin",
      lastName: "User",
      phone: "+1-555-0789",
      company: "Enterprise Corp",
    },
    items: [
      {
        id: "item-4",
        productId: "prod-1",
        productName: "Wireless Bluetooth Headphones",
        productSku: "WBH-001",
        quantity: 5,
        unitPrice: 199.99,
        totalPrice: 999.95,
        discount: 50.0,
        tax: 76.0,
      },
    ],
    shipping: {
      method: ShippingMethod.STANDARD,
      address: {
        firstName: "Admin",
        lastName: "User",
        company: "Enterprise Corp",
        address1: "789 Business Blvd",
        city: "New York",
        state: "NY",
        postalCode: "10001",
        country: "United States",
        phone: "+1-555-0789",
      },
      cost: 25.99,
    },
    billing: {
      address: {
        firstName: "Admin",
        lastName: "User",
        company: "Enterprise Corp",
        address1: "789 Business Blvd",
        city: "New York",
        state: "NY",
        postalCode: "10001",
        country: "United States",
        phone: "+1-555-0789",
      },
      method: BillingMethod.INVOICE,
    },
    payment: {
      method: PaymentMethod.INVOICE,
      status: PaymentStatus.PENDING,
      amount: 1051.94,
      currency: "USD",
    },
    total: {
      subtotal: 999.95,
      tax: 76.0,
      shipping: 25.99,
      discount: 50.0,
      total: 1051.94,
      currency: "USD",
    },
    status: OrderStatus.PROCESSING,
    notes: "Bulk order for corporate use",
    tags: ["bulk", "corporate"],
    createdAt: "2024-01-13T08:00:00Z",
    updatedAt: "2024-01-13T08:00:00Z",
  },
];

export const mockOrderResponse = {
  order: mockOrder,
  message: "Order retrieved successfully",
  success: true,
};

export const mockOrdersResponse = {
  orders: mockOrders,
  pagination: {
    page: 1,
    limit: 10,
    total: 3,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  },
  message: "Orders retrieved successfully",
  success: true,
};

export const mockOrderSearchResponse = {
  orders: mockOrders,
  filters: {
    statuses: [
      { status: OrderStatus.SHIPPED, count: 1 },
      { status: OrderStatus.DELIVERED, count: 1 },
      { status: OrderStatus.PROCESSING, count: 1 },
    ],
    paymentStatuses: [
      { status: PaymentStatus.COMPLETED, count: 2 },
      { status: PaymentStatus.PENDING, count: 1 },
    ],
    dateRange: {
      from: "2024-01-13T00:00:00Z",
      to: "2024-01-15T23:59:59Z",
    },
    totalRange: {
      min: 170.98,
      max: 1051.94,
    },
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 3,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  },
  message: "Orders search completed successfully",
  success: true,
};

export const mockOrderError = {
  message: "Order not found",
  success: false,
  status: 404,
  code: "ORDER_NOT_FOUND",
};
